import { Track } from "@/src/lib/tracking";
import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { dayjs } from "utils";
import { convertStringNumbersToNumbers } from "@utils/formatBigNumber";
import { StockMarketEnum, TradingData } from "../features/api/openData";
import { QUERY_KEYS } from "@config/constant";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { convertScheduleDataToTradingData } from "@utils/stock";

const PAGE_SIZE = 1;

export type MaybeTradingData = MaybeNullable<
  TradingData & Partial<ReturnType<typeof convertScheduleDataToTradingData>>
>;

interface QueryParamsType {
  pageSize?: number;
  enabled?: boolean;
  symbol?: string;
  queryParams?: any;
  ids?: string[];
  isPollToken?: boolean;
  queryKey?: string[];
  staleTime?: number;
  gcTime?: number;
}

export const useStockDetailsMarket = (params: QueryParamsType) => {
  const {
    enabled = true,
    queryParams,
    queryKey = [],
    symbol,
    staleTime = 60 * 1000,
    gcTime,
  } = params;
  const { openDataApi } = useOspApiClientProvider();
  const refetchTimer = useRef<NodeJS.Timeout | number | null>(null);

  const cacheTradingRef = useRef<Record<string, MaybeNullable<TradingData>>>(
    {},
  );

  const query = {
    limit: PAGE_SIZE,
    symbols: [symbol],
    ...queryParams,
  };

  const appliedQueryKey = [
    QUERY_KEYS.STOCK_DETAILS_MARKET,
    symbol,
    ...queryKey,
  ];

  const { data, isLoading, isFetching, isPending, isSuccess, refetch } =
    useQueries({
      queries: [
        {
          queryKey: [QUERY_KEYS.STOCK_MARKET_SCHEDULE, StockMarketEnum.US],
          staleTime: 60 * 60 * 1000, // 1小时
          queryFn: async () => {
            const { data } = await openDataApi!.stocks.getMarketSchedule({
              market: StockMarketEnum.US,
            });
            return data?.data;
          },
        },
        {
          queryKey: [QUERY_KEYS.STOCK_MARKET_SCHEDULE, StockMarketEnum.HK],
          staleTime: 60 * 60 * 1000, // 1小时
          queryFn: async () => {
            const { data } = await openDataApi!.stocks.getMarketSchedule({
              market: StockMarketEnum.HK,
            });
            return data?.data;
          },
        },
        {
          enabled: !!openDataApi && enabled,
          staleTime,
          gcTime,
          queryKey: appliedQueryKey,
          queryFn: async () => {
            try {
              const { data } = await openDataApi!.stocks.getStocks(query);
              const rows = data?.data?.rows || [];
              rows.forEach((item: any) => {
                if (!item.trading_data) {
                  item.trading_data = cacheTradingRef.current[item.symbol];
                } else {
                  cacheTradingRef.current[item.symbol] = item.trading_data;
                }
                if (!!item.financials?.items) {
                  item.financials.items = convertStringNumbersToNumbers(
                    item.financials.items,
                  );
                }
              });

              return rows?.[0];
            } catch (error) {
              Track.error(error, {
                tag: "Stock",
                msg: "useStockDetailsMarket",
              });
              return;
            }
          },
        },
      ],
      combine: (results) => {
        const [usData, hkData, stockData] = results;
        let tradingData: MaybeTradingData = stockData?.data?.trading_data;
        if (
          query["enrich.enable"] &&
          query["enrich.with_trading_data"] &&
          !!stockData.data &&
          !!usData?.data &&
          !!hkData?.data
        ) {
          tradingData = {
            ...tradingData,
            ...convertScheduleDataToTradingData(
              stockData?.data?.market === StockMarketEnum.HK
                ? hkData?.data
                : usData?.data,
            ),
          };
        }
        return {
          data: {
            ...stockData?.data,
            trading_data: tradingData,
          },
          isPending: results.some((result) => result.isPending),
          isError: results.some((result) => result.isError),
          isSuccess: results.every((result) => result.isSuccess),
          isFetching: results.some((result) => result.isFetching),
          isLoading: results.some((result) => result.isLoading),
          isRefetching: results.some((result) => result.isRefetching),
          isStale: results.some((result) => result.isStale),
          refetch: stockData?.refetch,
        };
      },
    });

  const refetchData = useCallback(() => {
    if (refetchTimer.current) {
      clearTimeout(refetchTimer.current as NodeJS.Timeout);
    }
    refetchTimer.current = null;

    const tradingData = data?.trading_data;
    const now = dayjs().valueOf();
    const timeList = [
      dayjs().utc().add(1, "day").startOf("day").valueOf(),
      tradingData?.regular_market_open_time,
      tradingData?.regular_market_close_time,
      tradingData?.post_market_close_time,
      tradingData?.pre_market_open_time,
      tradingData?.next_pre_market_open_time,
    ].sort((a, b) => {
      // 处理 undefined 值，将其排在最后
      if (a === undefined && b === undefined) {
        return 0;
      }
      if (a === undefined) {
        return 1;
      }
      if (b === undefined) {
        return -1;
      }
      // 对数字进行升序排序
      return a - b;
    });
    const minTime = timeList.find(
      (time) => typeof time === "number" && time > now,
    );

    // 计算timeout，确保是有效的setTimeout参数
    let timeout: number;
    // setTimeout最大值约为2147483647毫秒（约24.8天），超过则设为最大值
    const MAX_TIMEOUT = 2147483647;
    // 设置为15秒后,请求数据
    const timeDiff = minTime ? minTime - now + 15000 : MAX_TIMEOUT;

    timeout = Math.min(timeDiff, MAX_TIMEOUT);

    // 确保timeout至少为1毫秒，避免0或负值
    timeout = Math.max(timeout, 1);

    return minTime
      ? setTimeout(() => {
          refetch();
        }, timeout)
      : null;
  }, [refetch]);

  useEffect(() => {
    const tradingData = data?.trading_data;
    if (
      tradingData?.regular_market_open_time ||
      tradingData?.regular_market_close_time ||
      tradingData?.post_market_close_time ||
      tradingData?.pre_market_open_time ||
      tradingData?.next_pre_market_open_time
    ) {
      refetchTimer.current = refetchData();
    }

    return () => {
      if (refetchTimer.current) {
        clearTimeout(refetchTimer.current as NodeJS.Timeout);
      }
    };
  }, [refetchData, data]);
  return {
    data,
    isLoading,
    isFetching,
    isPending,
    isSuccess,
    queryKey: appliedQueryKey,
  };
};
